// ─── The gym's own exercise library (member side) ───
// `list_org_exercises` → { exercises: [{ id, exercise_id: 'org:<id>', name, cat, equip, lvl,
// tip, instructions, yt, demo: { url, content_type, duration_sec, expires_in } | null }] }.
// Coach programs reference them as exerciseId 'org:<id>'. To log one, the member app keeps
// a catalog-shaped copy in s.exercises (like a custom exercise) so history keeps its name;
// instructions and the demo video are always read live. No imports so
// tests/org-exercises.test.mjs can load this file directly in Node.

export const ORG_EXERCISE_PREFIX = "org:";
export const ORG_EXERCISES_CACHE_KEY = "ft-org-exercises";
export const isOrgExerciseId = (id) => typeof id === "string" && id.startsWith(ORG_EXERCISE_PREFIX) && id.length > ORG_EXERCISE_PREFIX.length;
export const orgExerciseId = (g) => (g && (g.exercise_id || (g.id ? ORG_EXERCISE_PREFIX + g.id : ""))) || "";

// Mirror of youtubeId in ironlog-api/api/_lib/org-exercises.mjs: an 11-character id from an id
// or any common YouTube URL, else null.
export function youtubeId(v) {
  if (typeof v !== "string") return null;
  const s = v.trim();
  if (/^[A-Za-z0-9_-]{11}$/.test(s)) return s;
  let u;
  try { u = new URL(s); } catch { return null; }
  const host = u.hostname.replace(/^www\.|^m\./, "");
  let id = null;
  if (host === "youtu.be") id = u.pathname.slice(1).split("/")[0];
  else if (host === "youtube.com" || host === "youtube-nocookie.com") {
    id = u.searchParams.get("v") || (u.pathname.match(/^\/(?:shorts|embed|live)\/([^/?#]+)/) || [])[1] || null;
  }
  return id && /^[A-Za-z0-9_-]{11}$/.test(id) ? id : null;
}
export const ytWatchUrl = (yt) => { const id = youtubeId(yt); return id ? `https://www.youtube.com/watch?v=${id}` : null; };
export const ytEmbedUrl = (yt) => { const id = youtubeId(yt); return id ? `https://www.youtube-nocookie.com/embed/${id}?rel=0&playsinline=1` : null; };

// The copy kept in s.exercises: the catalog shape only (no instructions, no expiring links).
export function toCatalogExercise(g) {
  const id = orgExerciseId(g);
  if (!isOrgExerciseId(id) || !g.name) return null;
  const out = { id, name: String(g.name), cat: g.cat || "Full Body", equip: g.equip || "Other", lvl: [1, 2, 3].includes(g.lvl) ? g.lvl : 1, gym: true };
  if (g.tip) out.tip = String(g.tip);
  const yt = youtubeId(g.yt);
  if (yt) out.yt = yt;
  return out;
}
export const gymCatalog = (list) => (Array.isArray(list) ? list : []).filter(g => g && !g.archived_at).map(toCatalogExercise).filter(Boolean);
export const gymById = (list) => Object.fromEntries((Array.isArray(list) ? list : []).filter(g => g && !g.archived_at).map(g => [orgExerciseId(g), g]));

// True when the stored copy differs from the gym's current version (renamed, new tip…).
const KEYS = ["name", "cat", "equip", "lvl", "tip", "yt"];
export const catalogDiffers = (a, b) => !a || !b || KEYS.some(k => (a[k] ?? null) !== (b[k] ?? null));

// s.exercises with the gym's exercises added or refreshed, for resolving a program's rows.
export function withGymExercises(exercises, gymList) {
  const base = Array.isArray(exercises) ? exercises : [];
  const gym = gymCatalog(gymList);
  if (!gym.length) return base;
  const byId = new Map(gym.map(g => [g.id, g]));
  const out = base.map(e => (byId.has(e.id) ? { ...e, ...byId.get(e.id) } : e));
  for (const g of gym) if (!base.some(e => e.id === g.id)) out.push(g);
  return out;
}

// The catalog entries to store before logging `ids`: every gym exercise among them that is
// missing from s.exercises or out of date. A program row naming an org exercise the list
// does not have (offline, not loaded yet) still gets a minimal entry from its own name.
export function gymEntriesToStore(exercises, gymList, rows) {
  const have = new Map((Array.isArray(exercises) ? exercises : []).map(e => [e.id, e]));
  const gym = new Map(gymCatalog(gymList).map(g => [g.id, g]));
  const out = [];
  const seen = new Set();
  for (const r of Array.isArray(rows) ? rows : []) {
    const id = r && (r.exerciseId || r.id);
    if (!isOrgExerciseId(id) || seen.has(id)) continue;
    seen.add(id);
    const want = gym.get(id) || (have.has(id) ? null : (r.name ? { id, name: String(r.name), cat: "Full Body", equip: "Other", lvl: 1, gym: true } : null));
    if (want && catalogDiffers(have.get(id), want)) out.push(want);
  }
  return out;
}

// Everything the app knows about one exercise id, for chips and detail views.
export function describeExercise(id, { exercises, gym } = {}) {
  const g = isOrgExerciseId(id) && gym ? gym[id] : null;
  const e = (Array.isArray(exercises) ? exercises : []).find(x => x.id === id) || null;
  if (!g && !e) return null;
  return {
    id, gym: !!(g || (e && e.gym) || isOrgExerciseId(id)),
    name: (g && g.name) || (e && e.name) || id,
    cat: (g && g.cat) || (e && e.cat) || null, equip: (g && g.equip) || (e && e.equip) || null,
    tip: (g ? g.tip : e && e.tip) || null, instructions: (g && g.instructions) || null,
    yt: youtubeId((g && g.yt) || (e && e.yt) || ""),
    demo: g && g.demo && g.demo.url ? g.demo : null,
  };
}
